import React, { memo, useEffect, useRef } from 'react';
import { Button, Stack } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

import { CardWrapper, FormAutocomplete, FormField, useHookForm } from '@monorepo/common';

import { useGetEmploymentTypesQuery, useGetIndustryQuery } from '../../../../services/servicePostAJob';
import { ArrowRight as ArrowRightIcon } from '../../../../components/Icons/arrow-right';
import { useAppSelector } from '../../../../store/hooks';
import { IEventsStepProps } from '../../interfaces';
import { IStep1Form } from './formInterface';
import { schema } from './schema';
import { useCommitOnLeave } from '../useCommitOnLeave';
import ListingTypeTitle from '../../../../components/JobStepper/components/ListingTypeTitle';
import { CompanyAutocomplete } from '../../../../components/CompanyAutocomplete';

interface IStep1Props extends IEventsStepProps {
  defaultData: IStep1Form,
  setStep1: (res: IStep1Form) => void;
  onSave?: (res: IStep1Form) => void;
  onDraftChange?: (res: IStep1Form) => void;
  registerCommit?: (commitNow: () => void) => () => void;
}
interface IOptions {
  selected?: boolean,
  value: string | number,
  text: string
}

export const Step1 = memo(({
  onNext,
  onSave,
  defaultData,
  setStep1,
  onDraftChange,
  registerCommit,
  Nav,
}:IStep1Props) => {
  const submitIntent = useRef<'next' | 'save'>('next');
  const { hostNumber, isHost } = useAppSelector(({ pathnameSettings }) => ({
    hostNumber: pathnameSettings.siteNumber.toString(),
    isHost: pathnameSettings.host,
  }));

  const { data: listIndustry, isLoading: isLoadingIndustry } = useGetIndustryQuery(hostNumber, {
    skip: !isHost
  });
  const { data: employmentTypeOptions, isLoading: isLoadingEmploymentType } = useGetEmploymentTypesQuery(null, {
    skip: !isHost
  });

  const {
    handleSubmit,
    getValues,
    register,
    setValue,
    errors,
    setFormData
  } = useHookForm<IStep1Form>({
    schema,
    defaultValues: defaultData,
    showChangedFields: onDraftChange,
  });

  const markCommitted = useCommitOnLeave(() => {
    const values = getValues();
    if (JSON.stringify(values) !== JSON.stringify(defaultData)) setStep1(values);
  }, registerCommit);

  const onSubmit = (data: IStep1Form) => {
    if (data && setStep1) {
      setStep1(data);
      if (submitIntent.current === 'save') {
        onSave?.(data);
      } else {
        markCommitted();
        onNext?.();
      }
      submitIntent.current = 'next';
    }
  };

  useEffect(() => {
    setFormData(defaultData);
  }, [defaultData]);

  return (
    <CardWrapper
      dataCy="step1"
      scrollOptions={{
        isErrorsAutoScrollPage: errors
      }}
    >
      <ListingTypeTitle />
      <form
        onSubmit={handleSubmit(onSubmit)}
        data-cy="step1"
      >
        <FormField
          isRequired
          extraProps={{ ...register('jobTitle') }}
          errors={errors}
          label="Job Title"
          maxLength={120}
        />
        <CompanyAutocomplete
          extraProps={{ ...register('companyName') }}
          supplierIdName="supplierId"
          supplierTitleName="supplierTitle"
          defaultSupplier={{ id: defaultData.supplierId, title: defaultData.supplierTitle }}
          setValue={setValue}
          getValues={getValues}
          errors={errors}
        />
        <FormAutocomplete<IOptions>
          optionsSettings={{
            optionText: 'text',
            optionValue: 'value'
          }}
          disableClearable
          isRequired
          isLoading={isLoadingIndustry}
          fieldComment="Please select the category that best fits your company and the role you’re hiring for."
          extraProps={{ ...register('industrySectorId') }}
          setValue={setValue}
          errors={errors}
          getValues={getValues}
          options={listIndustry || []}
          label="Job Category"
        />
        <FormAutocomplete<IOptions>
          optionsSettings={{
            optionText: 'text',
            optionValue: 'value'
          }}
          isLoading={isLoadingEmploymentType}
          isRequired
          disableClearable
          extraProps={{ ...register('employmentTypeId') }}
          errors={errors}
          getValues={getValues}
          options={employmentTypeOptions || []}
          label="Employment Type"
          setValue={setValue}
        />

        <Nav
          nextDisabled={false}
          nextButton={
            (
              <Stack
                className="cpg-edit-step-actions"
                direction={{ xs: 'column', sm: 'row' }}
              >
                <Button
                  startIcon={<SaveIcon />}
                  variant="outlined"
                  type="submit"
                  onClick={() => {
                    submitIntent.current = 'save';
                  }}
                  data-cy="saveListing"
                >
                  Save Listing
                </Button>
                <Button
                  endIcon={(<ArrowRightIcon fontSize="small" />)}
                  variant="contained"
                  disabled={false}
                  type="submit"
                  onClick={() => {
                    submitIntent.current = 'next';
                  }}
                  data-cy="nextStep"
                >
                  Next Step
                </Button>
              </Stack>
                )
          }
        />
      </form>
    </CardWrapper>
  );
});
